/**
 * React bindings for the organization scope.
 *
 * Kept apart from `orgScope.ts` so that module stays framework-free and cheap to
 * unit test, and apart from the barrel so importing a hook does not pull the
 * auth store into every store's module graph.
 */

import { useAuthStoreV1 } from '@/stores/authStore_v1';

import type { OrgId } from './types';

/**
 * The active organization's id, as a reactive value.
 *
 * Use it to scope a React Query key. Almost every request in this app carries
 * the active organization implicitly — `skillsAuthHeaders()` and
 * `testingAuthHeaders()` attach `X-Organization-ID` from the auth store — so a
 * query key that omits the org describes a response it cannot uniquely
 * identify, and React Query will happily serve Org A's list to Org B from cache.
 *
 * ```ts
 * const orgId = useActiveOrgId();
 * useQuery({ queryKey: agentKeys.list(orgId, params), queryFn: () => getAgents(params) });
 * ```
 */
export function useActiveOrgId(): OrgId | null {
  return useAuthStoreV1((state) => state.selectedOrg?.id ?? null);
}
